import * as React from 'react'
import { useCallback } from 'react'

import { Card } from '../../../../app/features/common/Card'
import { Toggle } from '../../../../app/features/common/Toggle'
import { ReportsList } from './ReportsList'
import { FooterWithButtons } from '../../components/FooterWithButtons'
import { PrintsIcon } from '@/app/features/icons/Print'
import { Button } from '@/app/features/common/Button'

import { getCopiesLabel } from './helper'

import { Header, Report, Reports } from '@/app/types/creator'
import { useParams } from 'react-router-dom'
import usePrintOutService from '@/modules/printouts/services/PrintOutService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  getReportNameByType,
  getReportTypeByNumber,
} from '@/app/services/printout/reportTypeResolver'
import { ReportType } from '@/app/constants/reportType'

const headers = [
  { name: 'name', label: 'NAZWA', sortable: true },
  { name: '', label: '', sortable: false,  },
]
type Props = {
  onPrev: () => void
  onNext: () => void
  selectedReports: string[]
  setSelectedReports: React.Dispatch<React.SetStateAction<string[]>>
  selectedDriversCount: number
  isGenerating?: boolean
  generateToSeparateFiles: boolean
  setGenerateToSeparateFiles: React.Dispatch<React.SetStateAction<boolean>>
}
export const SummaryFirstSubstep = ({
  onPrev,
  onNext,
  selectedReports,
  setSelectedReports,
  selectedDriversCount,
  isGenerating = false,
  generateToSeparateFiles,
  setGenerateToSeparateFiles,
}: Props) => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t, tSafe } = useLocalize()
  const { getFavPrintOuts } = usePrintOutService()
  const getFavPrintOutsRef = React.useRef(getFavPrintOuts)
  const [favouriteReports, setFavouriteReports] = React.useState<Report[]>([])

  React.useEffect(() => {
    getFavPrintOutsRef.current = getFavPrintOuts
  }, [getFavPrintOuts])

  const otherReports = React.useMemo<Report[]>(
    () => [
      { id: '8', name: t('printout.driverActivities') },
      { id: '15', name: t('printout.driverDataCompletness') },
      { id: '46', name: t('printout.driverCardReading') },
      { id: '9', name: t('printout.driverDailyActivities') },
      { id: '51', name: t('printout.driverFaultsAndEvent') },
      { id: '23', name: t('printout.violations') },
      { id: '24', name: t('printout.groupedViolations') },
      { id: '42', name: t('printout.localization') },
      { id: '13', name: t('printout.driverCalendar') },
      { id: '69', name: t('printout.driverPeriodDistance') },
      { id: '103', name: t('printout.timesheetPaymentDetails') },
      { id: '96', name: t('printout.rewardsToAcceptReport') },
      { id: '95', name: t('printout.mobilityPackagePayrollReport') },
    ],
    [t],
  )

  React.useEffect(() => {
    let isMounted = true

    const loadFavorites = async () => {
      try {
        const favReports = await getFavPrintOutsRef.current({ companyId })
        if (!Array.isArray(favReports) || !isMounted) {
          return
        }

        const uniqueIds = new Set<string>()
        const mapped = favReports
          .map((item: { reportType?: number; id?: string; name?: string }) => {
            const reportNumber = Number(item.reportType)
            if (Number.isNaN(reportNumber) || reportNumber <= 0) {
              return null
            }

            const reportType = getReportTypeByNumber(reportNumber)
            const reportName =
              reportType !== ReportType.NONE
                ? t(getReportNameByType(reportType))
                : item.name

            return {
              id: String(reportNumber),
              name: reportName || item.name || String(reportNumber),
            }
          })
          .filter((report): report is Report => {
            if (!report) {
              return false
            }

            if (uniqueIds.has(report.id)) {
              return false
            }

            uniqueIds.add(report.id)
            return true
          })

        setFavouriteReports(mapped)
      } catch (error) {
        console.error('Failed to load favorite printouts:', error)
      }
    }

    loadFavorites()

    return () => {
      isMounted = false
    }
  }, [companyId, t])

  const favouriteIds = React.useMemo(
    () => new Set(favouriteReports.map((report) => report.id)),
    [favouriteReports],
  )

  const reportItems: Reports = {
    favourite: favouriteReports,
    other: otherReports.filter((report) => !favouriteIds.has(report.id)),
  }

  const toggleSelectedReport = (id: string) => {
    setSelectedReports((prev: string[]) =>
      prev.includes(id)
        ? prev.filter((reportId) => reportId !== id)
        : [...prev, id],
    )
  }

  const printsNumber = selectedReports.length * selectedDriversCount
  const renderCell = useCallback(
    (row: Report, h: Header) => {
      if (h.name === 'toGenerate' || h.name === '')
        return (
          <div className="tw-flex tw-items-center tw-justify-end">
            <Toggle
              onChange={() => toggleSelectedReport(row.id)}
              checked={selectedReports.includes(row.id)}
            />
          </div>
        )
      return (
        <div className="tw-flex tw-items-center tw-py-3.5">
          {String(row[h.name] ?? '')}
        </div>
      )
    },
    [selectedReports],
  )

  return (
    <Card
      className="tw-inline-block tw-h-full tw-max-w-full tw-overflow-x-auto tw-bg-base-100 tw-pb-3"
      classNameContent="!tw-px-0 !tw-pt-0 tw-overflow-x-auto tw-flex tw-flex-col tw-h-full"
      paddingTop={false}
    >
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
        {reportItems.favourite.length > 0 && (
          <ReportsList
            headers={headers}
            reports={reportItems.favourite}
            renderCell={renderCell}
            isFavourite={true}
          />
        )}

        {reportItems.other.length > 0 && (
          <ReportsList
            title={tSafe('creator.summary.otherPrintouts', 'Pozostałe wydruki')}
            headers={headers}
            reports={reportItems.other}
            renderCell={renderCell}
          />
        )}
      </div>

      <div className="tw-border-base-30 tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-border-t tw-bg-base-100 tw-px-4 tw-py-3">
        <Toggle
          checked={generateToSeparateFiles}
          onChange={setGenerateToSeparateFiles}
          disabled={isGenerating}
        />
        <span className="tw-text-sm tw-text-base-content">
          {t('printout.generate-to-separate-files')}
        </span>
      </div>

      <div className="tw-shrink-0 tw-bg-base-100">
        <FooterWithButtons
          isNext={false}
          onPrev={onPrev}
          className="tw-border-base-30 tw-border-t"
          nextClassName="tw-min-w-44"
        >
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1 tw-px-4">
            <span>
              {tSafe(
                'creator.summary.totalGeneratedPrefix',
                'Łącznie wygenerowanych będzie',
              )}
            </span>
            <span className="tw-font-semibold">{printsNumber}</span>
            <span>{`${getCopiesLabel(printsNumber)} ${
              printsNumber === 1
                ? tSafe('creator.summary.printoutSingular', 'wydruku')
                : tSafe('creator.summary.printoutPlural', 'wydruków')
            } ${tSafe('creator.summary.for', 'dla')}`}</span>
            <span className="tw-font-semibold">{selectedDriversCount}</span>
            <span>
              {selectedDriversCount === 1
                ? tSafe('creator.summary.driverSingular', 'kierowcy')
                : tSafe('creator.common.driverPlural', 'kierowców')}
            </span>
          </div>
          <Button
            size="sm"
            onClick={onNext}
            disabled={isGenerating}
            className="!tw-h-10 tw-w-full tw-rounded-lg tw-font-medium sm:tw-w-fit"
          >
            <span className="tw-mb-0">
              {isGenerating
                ? tSafe('creator.common.generating', 'Generowanie...')
                : tSafe(
                    'creator.summary.generatePrintouts',
                    'Generuj wydruki',
                  )}
            </span>{' '}
            <PrintsIcon />
          </Button>
        </FooterWithButtons>
      </div>
    </Card>
  )
}
